// 115 — Bitácora UCI Pediátrica (Fase 4, Tanda 7a, ver PLAN.md). Bloque B
// (antropometría) y el alcance de Bloque C descrito en
// `bloques115Datos.ts` (datos de bitácora, diagnóstico, % de quemadura,
// gráfico de constantes, hemodinamia, medicación, ingresos, pérdidas,
// balance urinario + totales).
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CampoDictado } from './CampoDictado';
import { SelectorDiagnosticoCie10, type DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import {
  CAMPOS_ANTROPOMETRIA_115,
  CAMPOS_BALANCE_URINARIO_115,
  CAMPOS_TOTALES_115,
  clasificacionTiss28_115,
  COLUMNAS_EXAMENES_ESPECIALES_115,
  COLUMNAS_INTERCONSULTAS_115,
  COLUMNAS_PROCEDIMIENTOS_115,
  COLUMNAS_ULCERAS_115,
  DEFINICION_BRADEN_BERGSTOM_115,
  DEFINICION_DOLOR_SEDADO_115,
  DEFINICION_EVANS_115,
  DEFINICION_RAMSAY_115,
  FILAS_EXAMENES_ESPECIALES_115,
  FILAS_GASOMETRIA_115,
  FILAS_HEMODINAMIA_115,
  FILAS_INGRESOS_115,
  FILAS_INTERCONSULTAS_115,
  FILAS_LABORATORIO_115,
  FILAS_PUPILAS_115,
  FILAS_PROCEDIMIENTOS_OTROS_115,
  COLUMNAS_PROCEDIMIENTOS_OTROS_115,
  CAMPOS_TOTAL_DIA_115,
  totalDiaEfectivo115,
  type EstadoTotalDia115,
  type GruposNombrables115,
  FILAS_NEUROLOGICO_115,
  FILAS_PERDIDAS_115,
  FILAS_PROCEDIMIENTOS_115,
  FILAS_ULCERAS_115,
  FILAS_VENTILACION_ALTA_FRECUENCIA_115,
  FILAS_VENTILACION_CONVENCIONAL_115,
  GRUPOS_TISS28_115,
  ITEMS_CATETER_URINARIO_115,
  ITEMS_DISPOSITIVOS_VASCULARES_115,
  ITEMS_NEUMONIA_VENTILACION_115,
  ITEMS_PLAN_CUIDADOS_GENERALES_115,
  resumenTotalGlasgowPorHora115,
  SERIES_GRAFICO_CONSTANTES_115,
  totalTiss28_115,
  type EstadoAntropometria115,
  type EstadoBalanceUrinario115,
  type EstadoDatosBitacora115,
  type EstadoPrevencionInfecciones115,
  type EstadoResponsableEnfermeria115,
  type EstadoTiss28_115,
  type EstadoTotales115,
  type ItemPrevencionInfecciones115,
} from './bloques115Datos';
import { imcTexto } from './calculosClinicosDatos';
import { TablaHoraria24h, type EstadoTablaHoraria24h } from './uci/TablaHoraria24h';
import { GraficoEnfermeria, type PuntosGraficoEnfermeria } from './uci/GraficoEnfermeria';
import { EscalaClinica, type RespuestasEscalaClinica } from './uci/EscalaClinica';
import { ChecklistTurno, type EstadoChecklistTurno } from './uci/ChecklistTurno';
import { Checkbox } from '@/components/ui/checkbox';
import { TablaConColumnas, type EstadoTablaConColumnas } from './uci/TablaConColumnas';
import { CampoBalanceHidrico } from './uci/CampoBalanceHidrico';
import { CamposClinicosUci } from './uci/CamposClinicosUci';
import { FilasNombrablesHorarias } from './uci/FilasNombrablesHorarias';
import { SiluetaCorporal } from './SiluetaCorporal';

function CampoTexto({ id, label, value, onChange, tipo, soloLectura }: { readonly id: string; readonly label: string; readonly value: string; readonly onChange: (v: string) => void; readonly tipo?: string; readonly soloLectura?: boolean }): JSX.Element {
  if (tipo) {
    return (
      <div className="flex flex-col gap-1">
        <Label htmlFor={id}>{label}</Label>
        <Input id={id} type={tipo} value={value} readOnly={soloLectura} onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }
  return <CampoDictado id={id} etiqueta={label} valor={value} soloLectura={soloLectura} onChange={onChange} />;
}

function BloqueAntropometria({ estado, onChange }: { readonly estado: EstadoAntropometria115; readonly onChange: (v: EstadoAntropometria115) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.antropometriaTitulo')}</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        {CAMPOS_ANTROPOMETRIA_115.map((c) => (
          <CampoTexto
            key={c}
            id={`f115-antropometria-${c}`}
            label={t(`formularios.f115.antropometria.${c}`)}
            value={c === 'imc' ? imcTexto(estado.pesoReal, estado.talla) : estado[c]}
            soloLectura={c === 'imc'}
            onChange={(v) => onChange({ ...estado, [c]: v })}
          />
        ))}
      </div>
    </div>
  );
}

function BloqueDatosBitacora({ estado, onChange }: { readonly estado: EstadoDatosBitacora115; readonly onChange: (v: EstadoDatosBitacora115) => void }): JSX.Element {
  const { t } = useTranslation();
  function actualizar(cambios: Partial<EstadoDatosBitacora115>): void {
    onChange({ ...estado, ...cambios });
  }
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.datosBitacora.titulo')}</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        <CampoTexto id="f115-servicio" label={t('formularios.f115.datosBitacora.servicioProcedencia')} value={estado.servicioProcedencia} onChange={(v) => actualizar({ servicioProcedencia: v })} />
        <CampoTexto id="f115-fecha-ingreso" tipo="date" label={t('formularios.f115.datosBitacora.fechaIngresoUci')} value={estado.fechaIngresoUci} onChange={(v) => actualizar({ fechaIngresoUci: v })} />
        <CampoTexto id="f115-fecha-actual" tipo="date" label={t('formularios.f115.datosBitacora.fechaActual')} value={estado.fechaActual} onChange={(v) => actualizar({ fechaActual: v })} />
        <CamposClinicosUci prefijoId="f115" estado={estado} onChange={(v) => actualizar(v)} />
        <CampoTexto id="f115-porcentaje-quemadura" label={t('formularios.f115.datosBitacora.porcentajeQuemadura')} value={estado.porcentajeQuemadura} onChange={(v) => actualizar({ porcentajeQuemadura: v })} />
      </div>
    </div>
  );
}

function BloqueGraficoConstantes({ puntos, onChange }: { readonly puntos: PuntosGraficoEnfermeria; readonly onChange: (v: PuntosGraficoEnfermeria) => void }): JSX.Element {
  const { t } = useTranslation();
  const [serieActiva, setSerieActiva] = useState(SERIES_GRAFICO_CONSTANTES_115[0]!.clave);
  const [varianteActiva, setVarianteActiva] = useState<string | undefined>(SERIES_GRAFICO_CONSTANTES_115[0]!.variantes?.[0]?.clave);
  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.graficoConstantesTitulo')}</h4>
      <GraficoEnfermeria
        series={SERIES_GRAFICO_CONSTANTES_115}
        puntos={puntos}
        onChange={onChange}
        serieActiva={serieActiva}
        onChangeSerieActiva={(clave) => {
          setSerieActiva(clave);
          setVarianteActiva(SERIES_GRAFICO_CONSTANTES_115.find((s) => s.clave === clave)?.variantes?.[0]?.clave);
        }}
        varianteActiva={varianteActiva}
        onChangeVarianteActiva={setVarianteActiva}
      />
    </div>
  );
}

function BloqueBalanceUrinarioYTotales({
  balanceUrinario,
  onChangeBalanceUrinario,
  totales,
  onChangeTotales,
}: {
  readonly balanceUrinario: EstadoBalanceUrinario115;
  readonly onChangeBalanceUrinario: (v: EstadoBalanceUrinario115) => void;
  readonly totales: EstadoTotales115;
  readonly onChangeTotales: (v: EstadoTotales115) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.balanceUrinarioTitulo')}</h4>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {CAMPOS_BALANCE_URINARIO_115.map((c) => (
          <CampoTexto key={c} id={`f115-balance-urinario-${c}`} label={t(`formularios.f115.balanceUrinario.${c}`)} value={balanceUrinario[c]} onChange={(v) => onChangeBalanceUrinario({ ...balanceUrinario, [c]: v })} />
        ))}
      </div>
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.totalesTitulo')}</h4>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {CAMPOS_TOTALES_115.map((c) => (
          <CampoTexto key={c} id={`f115-totales-${c}`} label={t(`formularios.f115.totales.${c}`)} value={totales[c]} onChange={(v) => onChangeTotales({ ...totales, [c]: v })} />
        ))}
      </div>
    </div>
  );
}

function BloqueTiss28({ estado, onChange }: { readonly estado: EstadoTiss28_115; readonly onChange: (v: EstadoTiss28_115) => void }): JSX.Element {
  const { t } = useTranslation();
  const total = totalTiss28_115(estado);
  const clasificacion = clasificacionTiss28_115(total);
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.tiss28Titulo')}</h4>
      {GRUPOS_TISS28_115.map((grupo) => (
        <div key={grupo.clave} className="flex flex-col gap-2">
          <h5 className="text-xs font-medium text-hce-muted">{grupo.etiqueta}</h5>
          <EscalaClinica definicion={grupo.definicion} respuestas={estado[grupo.clave] ?? {}} onChange={(v) => onChange({ ...estado, [grupo.clave]: v })} />
        </div>
      ))}
      <div className="flex items-center justify-between border-t border-border pt-2 text-sm font-medium text-hce-ink">
        <span>{t('formularios.f115.tiss28Titulo')} — Total</span>
        <span>{total ?? '—'}</span>
      </div>
      {clasificacion && (
        <p className="text-sm text-hce-muted">
          Grado {clasificacion.grado} — {clasificacion.clasificacion} (relación enfermera-paciente {clasificacion.relacionEnfermeraPaciente})
        </p>
      )}
    </div>
  );
}

function GrupoPrevencionInfecciones({
  titulo,
  items,
  estado,
  onChange,
}: {
  readonly titulo: string;
  readonly items: readonly ItemPrevencionInfecciones115[];
  readonly estado: EstadoPrevencionInfecciones115;
  readonly onChange: (v: EstadoPrevencionInfecciones115) => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-hce-surface p-3">
      <h5 className="text-xs font-medium text-hce-muted">{titulo}</h5>
      {items.map((item) => (
        <div key={item.clave} className="flex items-center gap-2">
          <Checkbox
            id={`f115-infeccion-${item.clave}`}
            checked={estado[item.clave] ?? false}
            onCheckedChange={(v) => onChange({ ...estado, [item.clave]: v === true })}
          />
          <Label htmlFor={`f115-infeccion-${item.clave}`} className="text-sm font-normal">
            {item.etiqueta}
          </Label>
        </div>
      ))}
    </div>
  );
}

function BloquePrevencionInfecciones({ estado, onChange }: { readonly estado: EstadoPrevencionInfecciones115; readonly onChange: (v: EstadoPrevencionInfecciones115) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.prevencionInfeccionesTitulo')}</h4>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <GrupoPrevencionInfecciones titulo={t('formularios.f115.neumoniaVentilacionTitulo')} items={ITEMS_NEUMONIA_VENTILACION_115} estado={estado} onChange={onChange} />
        <GrupoPrevencionInfecciones titulo={t('formularios.f115.cateterUrinarioTitulo')} items={ITEMS_CATETER_URINARIO_115} estado={estado} onChange={onChange} />
        <GrupoPrevencionInfecciones titulo={t('formularios.f115.dispositivosVascularesTitulo')} items={ITEMS_DISPOSITIVOS_VASCULARES_115} estado={estado} onChange={onChange} />
      </div>
    </div>
  );
}

function BloqueResponsableEnfermeria115({ estado, onChange }: { readonly estado: EstadoResponsableEnfermeria115; readonly onChange: (v: EstadoResponsableEnfermeria115) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.responsableEnfermeriaTitulo')}</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <CampoTexto id="f115-resp-am" label={t('formularios.f115.responsableEnfermeria.am')} value={estado.am} onChange={(v) => onChange({ ...estado, am: v })} />
        <CampoTexto id="f115-resp-pm" label={t('formularios.f115.responsableEnfermeria.pm')} value={estado.pm} onChange={(v) => onChange({ ...estado, pm: v })} />
        <CampoTexto id="f115-resp-hs" label={t('formularios.f115.responsableEnfermeria.hs')} value={estado.hs} onChange={(v) => onChange({ ...estado, hs: v })} />
      </div>
    </div>
  );
}

export interface Bloques115Props {
  readonly antropometria: EstadoAntropometria115;
  readonly onChangeAntropometria: (v: EstadoAntropometria115) => void;
  readonly datosBitacora: EstadoDatosBitacora115;
  readonly onChangeDatosBitacora: (v: EstadoDatosBitacora115) => void;
  readonly diagnostico: DiagnosticoCie10Valor;
  readonly onChangeDiagnostico: (v: DiagnosticoCie10Valor) => void;
  readonly graficoConstantes: PuntosGraficoEnfermeria;
  readonly onChangeGraficoConstantes: (v: PuntosGraficoEnfermeria) => void;
  readonly hemodinamia: EstadoTablaHoraria24h;
  readonly onChangeHemodinamia: (v: EstadoTablaHoraria24h) => void;
  readonly grupos: GruposNombrables115;
  readonly onChangeGrupos: (v: GruposNombrables115) => void;
  readonly ingresos: EstadoTablaHoraria24h;
  readonly onChangeIngresos: (v: EstadoTablaHoraria24h) => void;
  readonly perdidas: EstadoTablaHoraria24h;
  readonly onChangePerdidas: (v: EstadoTablaHoraria24h) => void;
  readonly balanceUrinario: EstadoBalanceUrinario115;
  readonly onChangeBalanceUrinario: (v: EstadoBalanceUrinario115) => void;
  readonly totales: EstadoTotales115;
  readonly onChangeTotales: (v: EstadoTotales115) => void;
  readonly totalDia: EstadoTotalDia115;
  readonly onChangeTotalDia: (v: EstadoTotalDia115) => void;
  readonly ulceras: EstadoTablaConColumnas;
  readonly onChangeUlceras: (v: EstadoTablaConColumnas) => void;
  readonly siluetaUlceras: readonly string[];
  readonly onChangeSiluetaUlceras: (v: readonly string[]) => void;
  readonly bradenBergstom: RespuestasEscalaClinica;
  readonly onChangeBradenBergstom: (v: RespuestasEscalaClinica) => void;
  readonly ramsay: RespuestasEscalaClinica;
  readonly onChangeRamsay: (v: RespuestasEscalaClinica) => void;
  readonly evans: RespuestasEscalaClinica;
  readonly onChangeEvans: (v: RespuestasEscalaClinica) => void;
  readonly planCuidadosGenerales: EstadoChecklistTurno;
  readonly onChangePlanCuidadosGenerales: (v: EstadoChecklistTurno) => void;
  readonly examenesEspeciales: EstadoTablaConColumnas;
  readonly onChangeExamenesEspeciales: (v: EstadoTablaConColumnas) => void;
  readonly interconsultas: EstadoTablaConColumnas;
  readonly onChangeInterconsultas: (v: EstadoTablaConColumnas) => void;
  readonly ventilacionAltaFrecuencia: EstadoTablaHoraria24h;
  readonly onChangeVentilacionAltaFrecuencia: (v: EstadoTablaHoraria24h) => void;
  readonly ventilacionConvencional: EstadoTablaHoraria24h;
  readonly onChangeVentilacionConvencional: (v: EstadoTablaHoraria24h) => void;
  readonly neurologico: EstadoTablaHoraria24h;
  readonly onChangeNeurologico: (v: EstadoTablaHoraria24h) => void;
  readonly pupilas: EstadoTablaHoraria24h;
  readonly onChangePupilas: (v: EstadoTablaHoraria24h) => void;
  readonly laboratorio: EstadoTablaHoraria24h;
  readonly onChangeLaboratorio: (v: EstadoTablaHoraria24h) => void;
  readonly gasometria: EstadoTablaHoraria24h;
  readonly onChangeGasometria: (v: EstadoTablaHoraria24h) => void;
  readonly tiss28: EstadoTiss28_115;
  readonly onChangeTiss28: (v: EstadoTiss28_115) => void;
  readonly prevencionInfecciones: EstadoPrevencionInfecciones115;
  readonly onChangePrevencionInfecciones: (v: EstadoPrevencionInfecciones115) => void;
  readonly procedimientos: EstadoTablaConColumnas;
  readonly onChangeProcedimientos: (v: EstadoTablaConColumnas) => void;
  readonly procedimientosOtros: EstadoTablaConColumnas;
  readonly onChangeProcedimientosOtros: (v: EstadoTablaConColumnas) => void;
  readonly dolorSedado: RespuestasEscalaClinica;
  readonly onChangeDolorSedado: (v: RespuestasEscalaClinica) => void;
  readonly responsableEnfermeria: EstadoResponsableEnfermeria115;
  readonly onChangeResponsableEnfermeria: (v: EstadoResponsableEnfermeria115) => void;
}

export function Bloques115({
  antropometria,
  onChangeAntropometria,
  datosBitacora,
  onChangeDatosBitacora,
  diagnostico,
  onChangeDiagnostico,
  graficoConstantes,
  onChangeGraficoConstantes,
  hemodinamia,
  onChangeHemodinamia,
  grupos,
  onChangeGrupos,
  ingresos,
  onChangeIngresos,
  perdidas,
  onChangePerdidas,
  balanceUrinario,
  onChangeBalanceUrinario,
  totales,
  onChangeTotales,
  totalDia,
  onChangeTotalDia,
  ulceras,
  onChangeUlceras,
  siluetaUlceras,
  onChangeSiluetaUlceras,
  bradenBergstom,
  onChangeBradenBergstom,
  ramsay,
  onChangeRamsay,
  evans,
  onChangeEvans,
  planCuidadosGenerales,
  onChangePlanCuidadosGenerales,
  examenesEspeciales,
  onChangeExamenesEspeciales,
  interconsultas,
  onChangeInterconsultas,
  ventilacionAltaFrecuencia,
  onChangeVentilacionAltaFrecuencia,
  ventilacionConvencional,
  onChangeVentilacionConvencional,
  neurologico,
  onChangeNeurologico,
  pupilas,
  onChangePupilas,
  laboratorio,
  onChangeLaboratorio,
  gasometria,
  onChangeGasometria,
  tiss28,
  onChangeTiss28,
  prevencionInfecciones,
  onChangePrevencionInfecciones,
  procedimientos,
  onChangeProcedimientos,
  procedimientosOtros,
  onChangeProcedimientosOtros,
  dolorSedado,
  onChangeDolorSedado,
  responsableEnfermeria,
  onChangeResponsableEnfermeria,
}: Bloques115Props): JSX.Element {
  const { t } = useTranslation();
  const totalDiaCalculado = totalDiaEfectivo115(totalDia, ingresos, perdidas, grupos.ingresosLibres);
  const resumenGlasgow115 = resumenTotalGlasgowPorHora115(neurologico);
  return (
    <div className="flex flex-col gap-6">
      <BloqueAntropometria estado={antropometria} onChange={onChangeAntropometria} />
      <BloqueDatosBitacora estado={datosBitacora} onChange={onChangeDatosBitacora} />

      <div className="flex flex-col gap-3">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.diagnosticoIngreso')}</h4>
        <SelectorDiagnosticoCie10 label={t('formularios.f115.diagnosticoIngreso')} value={diagnostico} onChange={onChangeDiagnostico} />
      </div>

      <BloqueGraficoConstantes puntos={graficoConstantes} onChange={onChangeGraficoConstantes} />

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.hemodinamiaTitulo')}</h4>
        <TablaHoraria24h filas={FILAS_HEMODINAMIA_115} estado={hemodinamia} onChange={onChangeHemodinamia} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.medicacionTitulo')}</h4>
        <FilasNombrablesHorarias id="f115-medicacion" estado={grupos.medicacion} onChange={(v) => onChangeGrupos({ ...grupos, medicacion: v })} />
        <h5 className="text-xs font-medium text-hce-muted">{t('formularios.f115.nebulizacionTitulo')}</h5>
        <FilasNombrablesHorarias id="f115-nebulizacion" estado={grupos.nebulizacion} onChange={(v) => onChangeGrupos({ ...grupos, nebulizacion: v })} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.ingresosTitulo')}</h4>
        <h5 className="text-xs font-medium text-hce-muted">{t('formularios.f115.ingresosLibresTitulo')}</h5>
        <FilasNombrablesHorarias id="f115-ingresos-libres" unidad="mL" estado={grupos.ingresosLibres} onChange={(v) => onChangeGrupos({ ...grupos, ingresosLibres: v })} />
        <TablaHoraria24h filas={FILAS_INGRESOS_115} estado={ingresos} onChange={onChangeIngresos} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.perdidasTitulo')}</h4>
        <TablaHoraria24h filas={FILAS_PERDIDAS_115} estado={perdidas} onChange={onChangePerdidas} />
      </div>

      <BloqueBalanceUrinarioYTotales balanceUrinario={balanceUrinario} onChangeBalanceUrinario={onChangeBalanceUrinario} totales={totales} onChangeTotales={onChangeTotales} />

      <div className="flex flex-col gap-3">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.totalDiaTitulo')}</h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {CAMPOS_TOTAL_DIA_115.map((c) => (
            <CampoBalanceHidrico key={c} id={`f115-total-dia-${c}`} label={t(`formularios.f115.totalDia.${c}`)} manual={totalDia[c]} efectivo={totalDiaCalculado[c]} calculable onChange={(v) => onChangeTotalDia({ ...totalDia, [c]: v })} />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.ulcerasTitulo')}</h4>
        <SiluetaCorporal regiones={siluetaUlceras} onChange={onChangeSiluetaUlceras} />
        <TablaConColumnas filas={FILAS_ULCERAS_115} columnas={COLUMNAS_ULCERAS_115} estado={ulceras} onChange={onChangeUlceras} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.bradenBergstomTitulo')}</h4>
        <EscalaClinica definicion={DEFINICION_BRADEN_BERGSTOM_115} respuestas={bradenBergstom} onChange={onChangeBradenBergstom} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.ramsayTitulo')}</h4>
        <EscalaClinica definicion={DEFINICION_RAMSAY_115} respuestas={ramsay} onChange={onChangeRamsay} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.evansTitulo')}</h4>
        <EscalaClinica definicion={DEFINICION_EVANS_115} respuestas={evans} onChange={onChangeEvans} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.planCuidadosGeneralesTitulo')}</h4>
        <ChecklistTurno items={ITEMS_PLAN_CUIDADOS_GENERALES_115} estado={planCuidadosGenerales} onChange={onChangePlanCuidadosGenerales} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.examenesEspecialesTitulo')}</h4>
        <TablaConColumnas filas={FILAS_EXAMENES_ESPECIALES_115} columnas={COLUMNAS_EXAMENES_ESPECIALES_115} estado={examenesEspeciales} onChange={onChangeExamenesEspeciales} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.interconsultasTitulo')}</h4>
        <TablaConColumnas filas={FILAS_INTERCONSULTAS_115} columnas={COLUMNAS_INTERCONSULTAS_115} estado={interconsultas} onChange={onChangeInterconsultas} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.ventilacionAltaFrecuenciaTitulo')}</h4>
        <TablaHoraria24h filas={FILAS_VENTILACION_ALTA_FRECUENCIA_115} estado={ventilacionAltaFrecuencia} onChange={onChangeVentilacionAltaFrecuencia} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.ventilacionConvencionalTitulo')}</h4>
        <TablaHoraria24h filas={FILAS_VENTILACION_CONVENCIONAL_115} estado={ventilacionConvencional} onChange={onChangeVentilacionConvencional} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.neurologicoTitulo')}</h4>
        <TablaHoraria24h filas={FILAS_NEUROLOGICO_115} estado={neurologico} onChange={onChangeNeurologico} />
        <p className="text-xs text-hce-muted">
          {t('formularios.f115.neurologico.totalGlasgow')}: {resumenGlasgow115 || '—'}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.pupilasTitulo')}</h4>
        <TablaHoraria24h filas={FILAS_PUPILAS_115} estado={pupilas} onChange={onChangePupilas} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.laboratorioTitulo')}</h4>
        <TablaHoraria24h filas={FILAS_LABORATORIO_115} estado={laboratorio} onChange={onChangeLaboratorio} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.gasometriaTitulo')}</h4>
        <TablaHoraria24h filas={FILAS_GASOMETRIA_115} estado={gasometria} onChange={onChangeGasometria} />
      </div>

      <BloqueTiss28 estado={tiss28} onChange={onChangeTiss28} />

      <BloquePrevencionInfecciones estado={prevencionInfecciones} onChange={onChangePrevencionInfecciones} />

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.procedimientosTitulo')}</h4>
        <TablaConColumnas filas={FILAS_PROCEDIMIENTOS_115} columnas={COLUMNAS_PROCEDIMIENTOS_115} estado={procedimientos} onChange={onChangeProcedimientos} />
        <h5 className="text-xs font-medium text-hce-muted">{t('formularios.f115.procedimientosOtrosTitulo')}</h5>
        <TablaConColumnas filas={FILAS_PROCEDIMIENTOS_OTROS_115} columnas={COLUMNAS_PROCEDIMIENTOS_OTROS_115} estado={procedimientosOtros} onChange={onChangeProcedimientosOtros} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f115.dolorSedadoTitulo')}</h4>
        <EscalaClinica definicion={DEFINICION_DOLOR_SEDADO_115} respuestas={dolorSedado} onChange={onChangeDolorSedado} />
      </div>

      <BloqueResponsableEnfermeria115 estado={responsableEnfermeria} onChange={onChangeResponsableEnfermeria} />
    </div>
  );
}
